import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  CreditCard,
  Download,
  FileText,
  Files,
  House,
  Image,
  IndianRupee,
  Mail,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  Settings,
  Stethoscope,
  Trash2,
  UserCircle2,
  X,
  XCircle,
} from "lucide-react";
import DashboardLayout from "../layout/DashboardLayout";
import API from "../services/api";
import { canEditModule, getStoredUser, isAdminUser } from "../utils/auth";

const formatDate = (value) => {
  if (!value) {
    return "Unknown";
  }

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) {
    return "Unknown";
  }

  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const defaultBillingSettings = {
  homeVisitCharge: 500,
  clinicVisitCharge: 300,
  firstConsultationCharge: 200,
  discountType: "none",
  discountValue: 0,
};

const normalizeBillingSettings = (settings = {}) => ({
  homeVisitCharge: Number(settings.homeVisitCharge ?? defaultBillingSettings.homeVisitCharge),
  clinicVisitCharge: Number(settings.clinicVisitCharge ?? defaultBillingSettings.clinicVisitCharge),
  firstConsultationCharge: Number(
    settings.firstConsultationCharge ?? defaultBillingSettings.firstConsultationCharge
  ),
  discountType: ["percent", "amount"].includes(settings.discountType) ? settings.discountType : "none",
  discountValue: Number(settings.discountValue || 0),
});

const calculateTreatmentBilling = (plan, settingsOverride) => {
  const settings = normalizeBillingSettings(settingsOverride || plan?.billingSettings);
  const sessionCount = (plan?.sessionDays || []).filter((day) => day.date).length;
  const clinicSessionCount = (plan?.sessionDays || []).filter(
    (day) =>
      day.date &&
      String(day.treatmentLocation || plan?.treatmentLocation || "clinic").toLowerCase() !== "home"
  ).length;
  const homeVisitSessionCount = (plan?.sessionDays || []).filter(
    (day) =>
      day.date &&
      String(day.treatmentLocation || plan?.treatmentLocation || "clinic").toLowerCase() === "home"
  ).length;
  const sessionSubtotal =
    clinicSessionCount * settings.clinicVisitCharge + homeVisitSessionCount * settings.homeVisitCharge;
  const sessionRate = sessionCount > 0 ? sessionSubtotal / sessionCount : settings.clinicVisitCharge;
  const consultationCharge = settings.firstConsultationCharge;
  const discountAmount =
    settings.discountType === "percent"
      ? Math.min(sessionSubtotal, (sessionSubtotal * Math.min(settings.discountValue, 100)) / 100)
      : settings.discountType === "amount"
        ? Math.min(sessionSubtotal + consultationCharge, settings.discountValue)
        : 0;
  const payableAmount = Math.max(0, sessionSubtotal + consultationCharge - discountAmount);
  const paidAmount = (plan?.payments || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const balanceAmount = Math.max(0, payableAmount - paidAmount);
  const availableBalance = Math.max(0, paidAmount - payableAmount);

  return {
    settings,
    sessionCount,
    clinicSessionCount,
    homeVisitSessionCount,
    sessionRate,
    sessionSubtotal,
    consultationCharge,
    discountAmount,
    payableAmount,
    paidAmount,
    balanceAmount,
    availableBalance,
    availableSessionDays: sessionRate > 0 ? Math.ceil(availableBalance / sessionRate) : 0,
  };
};

const getAppointmentStatusLabel = (status) => {
  if (status === "completed") {
    return "Done";
  }

  if (status === "cancelled") {
    return "Cancelled";
  }

  if (status === "rescheduled") {
    return "Rescheduled";
  }

  if (status === "pending") {
    return "Pending";
  }

  return "Scheduled";
};

const formatServiceLocation = (value) =>
  String(value || "").toLowerCase() === "home" ? "At home" : "At clinic";

const appointmentLocationNote =
  "Home service is available after OPW confirms suitability. First-time patients and every post-session review must visit the clinic.";
const CLINICAL_NOTE_TYPES = ["C/C", "History", "O/E", "D/D", "HEP", "Advice"];

const getTodayKey = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const canEditPayments = canEditModule("payments", currentUser);
  const canDeletePayments = isAdminUser(currentUser);
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    address: "",
  });
  const [editBasic, setEditBasic] = useState(false);
  const [showClinicalNoteModal, setShowClinicalNoteModal] = useState(false);
  const [showClinicalDocumentModal, setShowClinicalDocumentModal] = useState(false);
  const [showTherapyModal, setShowTherapyModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    date: "",
    time: "",
    service: "",
    serviceLocation: "clinic",
  });
  const [services, setServices] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [therapyResources, setTherapyResources] = useState([]);
  const [appointmentActionForms, setAppointmentActionForms] = useState({});
  const [appointmentRequests, setAppointmentRequests] = useState([]);
  const [appointmentRequestForms, setAppointmentRequestForms] = useState({});
  const [clinicalNoteForm, setClinicalNoteForm] = useState({
    title: CLINICAL_NOTE_TYPES[0],
    note: "",
  });
  const [clinicalDocumentFiles, setClinicalDocumentFiles] = useState([]);
  const [therapyForm, setTherapyForm] = useState({
    serviceId: "",
    note: "",
    itemIds: [],
  });
  const [treatmentForm, setTreatmentForm] = useState({
    treatmentTypeInput: "",
    treatmentTypes: [],
    treatmentLocation: "clinic",
    assignedStaffId: "",
    fromDate: "",
    toDate: "",
    totalAmount: "",
    advanceAmount: "",
    paymentMethod: "",
    paymentNotes: "",
  });
  const [sessionEntryForm, setSessionEntryForm] = useState({
    planId: "",
    date: getTodayKey(),
    treatmentType: "",
    treatmentLocation: "clinic",
    doneByStaffId: "",
  });
  const [editingSessionDayId, setEditingSessionDayId] = useState("");
  const [editingPlanId, setEditingPlanId] = useState("");
  const [planPaymentForm, setPlanPaymentForm] = useState({
    planId: "",
    paymentId: "",
    amount: "",
    method: "",
    paymentDate: getTodayKey(),
  });
  const [billingSettingsModalPlanId, setBillingSettingsModalPlanId] = useState("");
  const [billingSettingsForm, setBillingSettingsForm] = useState(defaultBillingSettings);
  const [treatmentActionConfirm, setTreatmentActionConfirm] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadPatient = useCallback(async () => {
    try {
      const [response, requestResponse] = await Promise.all([
        API.get(`/patients/${id}`),
        API.get(`/patients/${id}/appointment-requests`),
      ]);
      setPatient(response.data);
      setAppointmentRequests(requestResponse.data || []);
      setAppointmentRequestForms((current) => {
        const next = { ...current };
        (requestResponse.data || []).forEach((request) => {
          if (!next[request.id]) {
            next[request.id] = {
              date: request.confirmedDate || request.requestedDate || "",
              time: request.confirmedTime || request.requestedTime || "",
              note: request.decisionNote || "",
            };
          }
        });
        return next;
      });
      setAppointmentActionForms((current) => {
        const next = { ...current };
        (response.data.appointments || []).forEach((appointment) => {
          if (!next[appointment.id]) {
            next[appointment.id] = {
              date: appointment.date || "",
              time: appointment.time || "",
              remark: appointment.remark || "",
            };
          }
        });
        return next;
      });
      setForm({
        name: response.data.name,
        email: response.data.email,
        mobile: response.data.mobile,
        address: response.data.address || "",
      });
      setError("");
    } catch (loadError) {
      setError(loadError.response?.data?.message || "Failed to load patient profile.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [servicesResponse, therapyResponse, staffResponse] = await Promise.allSettled([
          API.get("/services"),
          API.get("/therapy-resources"),
          API.get("/users"),
        ]);
        setServices(servicesResponse.status === "fulfilled" ? servicesResponse.value.data || [] : []);
        setTherapyResources(
          therapyResponse.status === "fulfilled" ? therapyResponse.value.data || [] : []
        );
        setStaffOptions(
          staffResponse.status === "fulfilled"
            ? (staffResponse.value.data || []).filter(
                (user) => user.role !== "Admin" && String(user.status || "Active") === "Active"
              )
            : []
        );
      } catch (_) {
        setServices([]);
        setTherapyResources([]);
        setStaffOptions([]);
      }
    };

    loadReferenceData();
  }, []);

  const closeDrawer = () => {
    setEditBasic(false);
    setShowClinicalNoteModal(false);
    setShowClinicalDocumentModal(false);
    setShowTherapyModal(false);
    setShowAppointmentModal(false);
    setShowTreatmentModal(false);
    setEditingPlanId("");
    setBillingSettingsModalPlanId("");
    setBillingSettingsForm(defaultBillingSettings);
    setPlanPaymentForm({ planId: "", paymentId: "", amount: "", method: "", paymentDate: getTodayKey() });
    setSessionEntryForm({
      planId: "",
      date: getTodayKey(),
      treatmentType: "",
      treatmentLocation: "clinic",
      doneByStaffId: "",
    });
    setEditingSessionDayId("");
    setClinicalNoteForm({
      title: CLINICAL_NOTE_TYPES[0],
      note: "",
    });
    setClinicalDocumentFiles([]);
    setTherapyForm({
      serviceId: "",
      note: "",
      itemIds: [],
    });
    setTreatmentForm({
      treatmentTypeInput: "",
      treatmentTypes: [],
      treatmentLocation: "clinic",
      assignedStaffId: "",
      fromDate: "",
      toDate: "",
      totalAmount: "",
      advanceAmount: "",
      paymentMethod: "",
      paymentNotes: "",
    });
  };

  const handleBasicUpdate = async (e) => {
    e.preventDefault();

    try {
      const response = await API.put(`/patients/${id}`, {
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        address: form.address,
      });
      setPatient(response.data);
      setForm((current) => ({ ...current, ...response.data }));
      setEditBasic(false);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to update patient.");
    }
  };

  const handleAddClinicalNote = async (e) => {
    e.preventDefault();

    try {
      const payload = new FormData();
      payload.append("title", clinicalNoteForm.title);
      payload.append("note", clinicalNoteForm.note);
      payload.append("addedByType", "opw");
      payload.append("addedByLabel", "OPW");

      const response = await API.post(`/patients/${id}/clinical-notes`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setPatient(response.data);
      setClinicalNoteForm({
        title: CLINICAL_NOTE_TYPES[0],
        note: "",
      });
      setShowClinicalNoteModal(false);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to save clinical note.");
    }
  };

  const handleAddClinicalDocuments = async (e) => {
    e.preventDefault();

    if (!clinicalDocumentFiles.length) {
      setError("Please choose at least one image or PDF document.");
      return;
    }

    try {
      const payload = new FormData();
      clinicalDocumentFiles.forEach((file) => {
        payload.append("documents", file);
      });

      const response = await API.post(`/patients/${id}/clinical-documents`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setPatient(response.data);
      setClinicalDocumentFiles([]);
      setShowClinicalDocumentModal(false);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to upload clinical documents.");
    }
  };

  const handleDeleteClinicalDocument = async (documentId) => {
    try {
      const response = await API.delete(`/patients/${id}/clinical-documents/${documentId}`);
      setPatient(response.data);
      setError("");
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || "Failed to delete clinical document.");
    }
  };

  const handleDeleteClinicalNote = async (noteId) => {
    try {
      const response = await API.delete(`/patients/${id}/clinical-notes/${noteId}`);
      setPatient(response.data);
      setError("");
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || "Failed to delete clinical note.");
    }
  };

  const openTherapyModal = () => {
    setTherapyForm({
      serviceId: services[0]?.id || "",
      note: "",
      itemIds: [],
    });
    setShowTherapyModal(true);
  };

  const toggleTherapyItem = (itemId) => {
    setTherapyForm((current) => ({
      ...current,
      itemIds: current.itemIds.includes(itemId)
        ? current.itemIds.filter((entry) => entry !== itemId)
        : [...current.itemIds, itemId],
    }));
  };

  const handleSaveTherapyRecommendation = async (event) => {
    event.preventDefault();

    try {
      const response = await API.post(`/patients/${id}/therapy-recommendations`, {
        serviceId: therapyForm.serviceId,
        note: therapyForm.note,
        itemIds: therapyForm.itemIds,
      });

      setPatient(response.data);
      setShowTherapyModal(false);
      setTherapyForm({
        serviceId: "",
        note: "",
        itemIds: [],
      });
      setError("");
    } catch (saveError) {
      setError(
        saveError.response?.data?.message || "Failed to save therapy recommendation."
      );
    }
  };

  const handleDeleteTherapyRecommendation = async (recommendationId) => {
    try {
      const response = await API.delete(
        `/patients/${id}/therapy-recommendations/${recommendationId}`
      );
      setPatient(response.data);
      setError("");
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.message || "Failed to delete therapy recommendation."
      );
    }
  };

  const handleStartTreatment = async () => {
    try {
      const response = await API.post(`/patients/${id}/treatment-plans`, {});

      setPatient(response.data);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to start treatment.");
    }
  };

  const resetSessionEntryForm = (planId = "") => {
    setSessionEntryForm({
      planId,
      date: getTodayKey(),
      treatmentType: "",
      treatmentLocation: "clinic",
      doneByStaffId: "",
    });
    setEditingSessionDayId("");
  };

  const startEditingSessionEntry = (plan, day) => {
    setSessionEntryForm({
      planId: plan.id,
      date: day.date || getTodayKey(),
      treatmentType: day.treatmentType || "",
      treatmentLocation: day.treatmentLocation || plan.treatmentLocation || "clinic",
      doneByStaffId: day.doneByStaffId || "",
    });
    setEditingSessionDayId(day.id);
    setError("");
  };

  const handleSaveSessionEntry = async (event, planId) => {
    event.preventDefault();

    try {
      const payload = {
        date: sessionEntryForm.date || getTodayKey(),
        treatmentType: sessionEntryForm.treatmentType,
        treatmentLocation: sessionEntryForm.treatmentLocation || "clinic",
        doneByStaffId: sessionEntryForm.doneByStaffId,
      };
      const response = editingSessionDayId && sessionEntryForm.planId === planId
        ? await API.put(
            `/patients/${id}/treatment-plans/${planId}/session-days/${editingSessionDayId}`,
            payload
          )
        : await API.post(`/patients/${id}/treatment-plans/${planId}/session-days`, payload);

      setPatient(response.data);
      resetSessionEntryForm(planId);
      setError("");
    } catch (saveError) {
      setError(
        saveError.response?.data?.message ||
          (editingSessionDayId
            ? "Failed to update treatment done details."
            : "Failed to add treatment done details.")
      );
    }
  };

  const handleUpdateTreatment = async (e) => {
    e.preventDefault();

    try {
      const response = await API.put(`/patients/${id}/treatment-plans/${editingPlanId}`, {
        treatmentLocation: treatmentForm.treatmentLocation,
      });

      setPatient(response.data);
      setTreatmentForm({
        treatmentTypeInput: "",
        treatmentTypes: [],
        treatmentLocation: "clinic",
        assignedStaffId: "",
        fromDate: "",
        toDate: "",
        totalAmount: "",
        advanceAmount: "",
        paymentMethod: "",
        paymentNotes: "",
      });
      setEditingPlanId("");
      setShowTreatmentModal(false);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to update treatment.");
    }
  };

  const handleTreatmentStatus = async (planId, status) => {
    try {
      const response = await API.patch(`/patients/${id}/treatment-plans/${planId}/status`, {
        status,
      });
      setPatient(response.data);
      setError("");
    } catch (updateError) {
      setError(updateError.response?.data?.message || "Failed to update treatment status.");
    }
  };

  const handleTreatmentModeChange = async (planId, treatmentLocation) => {
    try {
      const response = await API.put(`/patients/${id}/treatment-plans/${planId}`, {
        treatmentLocation,
      });
      setPatient(response.data);
      setError("");
    } catch (updateError) {
      setError(updateError.response?.data?.message || "Failed to update treatment mode.");
    }
  };

  const openBillingSettingsModal = (plan) => {
    setBillingSettingsModalPlanId(plan.id);
    setBillingSettingsForm(normalizeBillingSettings(plan.billingSettings));
  };

  const updateBillingSettingsForm = (key, value) => {
    setBillingSettingsForm((current) => ({
      ...current,
      [key]: key === "discountType" ? value : value.replace(/[^\d.]/g, ""),
    }));
  };

  const handleSaveBillingSettings = async (event) => {
    event.preventDefault();

    try {
      const response = await API.put(`/patients/${id}/treatment-plans/${billingSettingsModalPlanId}`, {
        billingSettings: normalizeBillingSettings(billingSettingsForm),
      });
      setPatient(response.data);
      setBillingSettingsModalPlanId("");
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to update payment settings.");
    }
  };

  const handleDeleteTreatmentPlan = async (planId) => {
    try {
      const response = await API.delete(`/patients/${id}/treatment-plans/${planId}`);
      setPatient(response.data);
      setError("");
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || "Failed to delete treatment plan.");
    }
  };

  const confirmTreatmentAction = async () => {
    const action = treatmentActionConfirm;
    setTreatmentActionConfirm(null);

    if (!action?.planId) {
      return;
    }

    if (action.type === "complete") {
      await handleTreatmentStatus(action.planId, "completed");
      return;
    }

    if (action.type === "delete") {
      await handleDeleteTreatmentPlan(action.planId);
    }
  };

  const handleAddPlanPayment = async (e) => {
    e.preventDefault();

    try {
      const selectedPlan = (patient.treatmentPlans || []).find(
        (plan) => plan.id === planPaymentForm.planId
      );
      const payload = {
        amount: Number(planPaymentForm.amount || 0),
        method: planPaymentForm.method,
        paymentDate: planPaymentForm.paymentDate || getTodayKey(),
        billingSettings: normalizeBillingSettings(selectedPlan?.billingSettings),
      };
      const response = planPaymentForm.paymentId
        ? await API.put(
            `/patients/${id}/treatment-plans/${planPaymentForm.planId}/payments/${planPaymentForm.paymentId}`,
            payload
          )
        : await API.post(
            `/patients/${id}/treatment-plans/${planPaymentForm.planId}/payments`,
            payload
          );

      setPatient(response.data);
      setPlanPaymentForm({ planId: "", paymentId: "", amount: "", method: "", paymentDate: getTodayKey() });
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to save payment.");
    }
  };

  const handleEditPlanPayment = (plan, payment) => {
    setPlanPaymentForm({
      planId: plan.id,
      paymentId: payment.id,
      amount: String(payment.amount || ""),
      method: payment.method || plan.paymentMethod || "",
      paymentDate: payment.paymentDate || getTodayKey(),
    });
  };

  const handleDeletePlanPayment = async (planId, paymentId) => {
    if (!window.confirm("Delete this payment entry?")) {
      return;
    }

    try {
      const response = await API.delete(
        `/patients/${id}/treatment-plans/${planId}/payments/${paymentId}`
      );
      setPatient(response.data);
      if (planPaymentForm.paymentId === paymentId) {
        setPlanPaymentForm({ planId: "", paymentId: "", amount: "", method: "", paymentDate: getTodayKey() });
      }
      setError("");
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || "Failed to delete payment.");
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();

    try {
      const response = await API.post(`/patients/${id}/appointments`, appointmentForm);
      setPatient(response.data);
      setAppointmentForm({ date: "", time: "", service: "", serviceLocation: "clinic" });
      setShowAppointmentModal(false);
      setError("");
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Failed to add appointment.");
    }
  };

  const updateAppointmentActionForm = (appointmentId, key, value) => {
    setAppointmentActionForms((current) => ({
      ...current,
      [appointmentId]: {
        ...(current[appointmentId] || {}),
        [key]: value,
      },
    }));
  };

  const updateAppointmentRequestForm = (requestId, key, value) => {
    setAppointmentRequestForms((current) => ({
      ...current,
      [requestId]: {
        ...(current[requestId] || {}),
        [key]: value,
      },
    }));
  };

  const handleAppointmentRequestDecision = async (request, action) => {
    const form = appointmentRequestForms[request.id] || {};
    const date = form.date || request.requestedDate || "";

    if (!date) {
      setError("Please select a date before updating this request.");
      return;
    }

    try {
      await API.patch(`/appointments/${request.id}/${action}`, {
        date,
        time: form.time || request.requestedTime || "",
        note: form.note || "",
      });
      await loadPatient();
      setError("");
    } catch (updateError) {
      setError(updateError.response?.data?.message || "Failed to update appointment request.");
    }
  };

  const handleAppointmentStatus = async (appointment, status) => {
    const form = appointmentActionForms[appointment.id] || {};
    const date = form.date || appointment.date || "";
    const time = form.time || appointment.time || "";
    const remark = form.remark || "";

    if (status === "rescheduled" && !date) {
      setError("Please select a date before rescheduling this appointment.");
      return;
    }

    if (["completed", "cancelled"].includes(status) && !remark.trim()) {
      setError("Please add a remark before updating this appointment.");
      return;
    }

    try {
      const response = await API.patch(`/patients/${id}/appointments/${appointment.id}`, {
        status,
        date,
        time,
        remark,
        serviceLocation: appointment.serviceLocation || "clinic",
      });
      setPatient(response.data);
      setError("");
    } catch (updateError) {
      setError(updateError.response?.data?.message || "Failed to update appointment.");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 shadow-sm">
          Loading patient profile...
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <button
            onClick={() => navigate("/patients")}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700"
          >
            <ArrowLeft size={16} /> Back to Patients
          </button>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error || "Patient not found."}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const activeTreatmentCount = (patient.treatmentPlans || []).filter(
    (plan) => plan.status === "active"
  ).length;
  const activeAppointments = (patient.appointments || []).filter(
    (appointment) => !["completed", "cancelled"].includes(appointment.status || "")
  );
  const todayKey = getTodayKey();
  const appointmentHistory = (patient.appointments || []).filter((appointment) =>
    ["completed", "cancelled"].includes(appointment.status || "")
  );
  const linkedAppointmentRequestIds = new Set(
    (patient.appointments || []).map((appointment) => appointment.requestId).filter(Boolean)
  );
  const visibleAppointmentRequests = appointmentRequests.filter(
    (request) =>
      !linkedAppointmentRequestIds.has(request.id) &&
      (!request.status || request.status === "pending")
  );
  const treatmentPlans = patient.treatmentPlans || [];
  const treatmentPaymentCount = treatmentPlans.reduce(
    (sum, plan) => sum + (plan.payments?.length || 0),
    0
  );
  const treatmentBillingSummaries = treatmentPlans.map((plan) => calculateTreatmentBilling(plan));
  const treatmentPaidTotal = treatmentBillingSummaries.reduce(
    (sum, billing) => sum + billing.paidAmount,
    0
  );
  const treatmentBalanceTotal = treatmentBillingSummaries.reduce(
    (sum, billing) => sum + billing.balanceAmount,
    0
  );
  const treatmentAvailableTotal = treatmentBillingSummaries.reduce(
    (sum, billing) => sum + billing.availableBalance,
    0
  );
  const treatmentAvailableDays = treatmentBillingSummaries.reduce(
    (sum, billing) => sum + billing.availableSessionDays,
    0
  );
  const selectedPaymentPlan = treatmentPlans.find((plan) => plan.id === planPaymentForm.planId);
  const selectedPaymentBilling = selectedPaymentPlan
    ? calculateTreatmentBilling(selectedPaymentPlan)
    : null;
  const billingSettingsPlan = treatmentPlans.find((plan) => plan.id === billingSettingsModalPlanId);
  const billingSettingsPreview = billingSettingsPlan
    ? calculateTreatmentBilling(billingSettingsPlan, billingSettingsForm)
    : null;

  const summaryCards = [
    {
      label: "Patient ID",
      value: patient.patientId || "Not assigned",
      icon: UserCircle2,
      tone: "bg-blue-50 text-blue-600",
    },
    {
      label: "Active Treatments",
      value: activeTreatmentCount,
      icon: Stethoscope,
      tone: "bg-cyan-50 text-cyan-700",
    },
    {
      label: "Session Payments",
      value: treatmentPaymentCount,
      icon: CreditCard,
      tone: "bg-amber-50 text-amber-600",
    },
    {
      label: "Clinical Notes",
      value: patient.clinicalNotes?.length || 0,
      icon: Files,
      tone: "bg-violet-50 text-violet-600",
    },
  ];

  const availableTherapyResources = therapyForm.serviceId
    ? therapyResources.filter((resource) => resource.serviceId === therapyForm.serviceId)
    : [];

  const panelClass = "rounded-2xl border border-slate-200/80 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.06)]";

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-3 overflow-x-hidden">
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {billingSettingsModalPlanId && billingSettingsPlan ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 px-3 py-4 backdrop-blur-sm sm:items-center">
            <form
              onSubmit={handleSaveBillingSettings}
              className="w-full max-w-2xl rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.25)]"
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                    <Settings size={14} />
                    Payment Settings
                  </div>
                  <h3 className="mt-3 text-xl font-semibold text-slate-950">
                    Auto calculate treatment payment
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Uses started days from the treatment session and the selected clinic/home visit mode.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setBillingSettingsModalPlanId("")}
                  className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Home Visit
                  </span>
                  <input
                    className="input mt-2"
                    value={billingSettingsForm.homeVisitCharge}
                    onChange={(event) => updateBillingSettingsForm("homeVisitCharge", event.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Clinic Visit
                  </span>
                  <input
                    className="input mt-2"
                    value={billingSettingsForm.clinicVisitCharge}
                    onChange={(event) => updateBillingSettingsForm("clinicVisitCharge", event.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    1st Consultant
                  </span>
                  <input
                    className="input mt-2"
                    value={billingSettingsForm.firstConsultationCharge}
                    onChange={(event) => updateBillingSettingsForm("firstConsultationCharge", event.target.value)}
                  />
                </label>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr]">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Discount Type
                  </span>
                  <select
                    className="input mt-2"
                    value={billingSettingsForm.discountType}
                    onChange={(event) => updateBillingSettingsForm("discountType", event.target.value)}
                  >
                    <option value="none">No discount</option>
                    <option value="percent">Percentage</option>
                    <option value="amount">Money amount</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Discount Value
                  </span>
                  <input
                    className="input mt-2"
                    value={billingSettingsForm.discountValue}
                    onChange={(event) => updateBillingSettingsForm("discountValue", event.target.value)}
                    disabled={billingSettingsForm.discountType === "none"}
                  />
                </label>
              </div>

              {billingSettingsPreview ? (
                <div className="mt-4 rounded-2xl border border-cyan-100 bg-cyan-50/60 p-4">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-cyan-700">Started Days</p>
                      <p className="text-lg font-semibold text-slate-950">{billingSettingsPreview.sessionCount}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-cyan-700">Session Amount</p>
                      <p className="text-lg font-semibold text-slate-950">{formatMoney(billingSettingsPreview.sessionSubtotal)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-cyan-700">Discount</p>
                      <p className="text-lg font-semibold text-slate-950">{formatMoney(billingSettingsPreview.discountAmount)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-cyan-700">Payable</p>
                      <p className="text-lg font-semibold text-slate-950">{formatMoney(billingSettingsPreview.payableAmount)}</p>
                    </div>
                  </div>
                  {billingSettingsForm.discountType === "percent" ? (
                    <p className="mt-3 text-xs font-medium text-cyan-800">
                      Percentage discount applies only to session charges. Consultant charge is not counted for percentage discount.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setBillingSettingsModalPlanId("")}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800"
                >
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {treatmentActionConfirm ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 px-3 py-4 backdrop-blur-sm sm:items-center">
            <div className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.25)]">
              <div className="flex items-start gap-3">
                <div
                  className={`rounded-2xl p-3 ${
                    treatmentActionConfirm.type === "delete"
                      ? "bg-rose-50 text-rose-600"
                      : "bg-emerald-50 text-emerald-600"
                  }`}
                >
                  {treatmentActionConfirm.type === "delete" ? (
                    <Trash2 size={20} />
                  ) : (
                    <CheckCircle2 size={20} />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">
                    {treatmentActionConfirm.type === "delete"
                      ? "Delete treatment session?"
                      : "Mark session completed?"}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    {treatmentActionConfirm.type === "delete"
                      ? "This will remove the treatment session from this patient profile."
                      : "Completed sessions cannot be reactivated. Create a new session when treatment restarts."}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setTreatmentActionConfirm(null)}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmTreatmentAction}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white ${
                    treatmentActionConfirm.type === "delete"
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {treatmentActionConfirm.type === "delete" ? "Delete" : "Mark Completed"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.22),transparent_32%),linear-gradient(120deg,#020617,#0f172a_52%,#075985)] px-4 py-3 text-white shadow-[0_16px_48px_rgba(2,6,23,0.16)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/12 ring-1 ring-white/20 backdrop-blur">
                <span className="text-lg font-semibold">{patient.name.charAt(0)}</span>
              </div>

              <div className="space-y-0.5">
                <button
                  onClick={() => navigate("/patients")}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 hover:bg-white/15"
                >
                  <ArrowLeft size={16} />
                  Back to Patients
                </button>

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                    Patient Profile
                  </p>
                  <h1 className="text-xl font-semibold tracking-tight">{patient.name}</h1>
                </div>

                <div className="flex flex-wrap gap-2 text-xs text-white/75">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                    <Mail size={14} />
                    {patient.email}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                    <Phone size={14} />
                    {patient.mobile}
                  </span>
                  {patient.address ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                      {patient.address}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <button
              onClick={() => setEditBasic(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-100"
            >
              <Pencil size={16} />
              Edit Profile
            </button>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map(({ label, value, icon: Icon, tone }) => (
            <div key={label} className={`${panelClass} p-3`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
                  <p className="mt-0.5 text-xl font-semibold text-slate-900">{value}</p>
                </div>
                <div className={`rounded-xl p-2 ${tone}`}>
                  <Icon size={17} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid min-w-0 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,430px)]">
          <div className="min-w-0 space-y-4">
            <section className="rounded-[28px] border border-cyan-100/80 bg-white p-4 shadow-[0_18px_50px_rgba(15,118,110,0.08)]">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                    <Stethoscope size={14} />
                    Treatment
                  </div>
                  <h2 className="mt-3 text-xl font-semibold text-slate-950">Session progress</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Start an active session, then add date-wise treatment done details below it.
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (activeTreatmentCount > 0) {
                      setError("A treatment session is already active. Complete it before starting another one.");
                      return;
                    }

                    handleStartTreatment();
                  }}
                  disabled={activeTreatmentCount > 0}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                >
                  <Plus size={15} />
                  {activeTreatmentCount > 0 ? "Active Session Running" : "Session Start"}
                </button>
              </div>

              <div className="space-y-4">
                {!patient.treatmentPlans?.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    No treatment session started yet.
                  </div>
                ) : (
                  patient.treatmentPlans.map((plan) => {
                    const planBilling = calculateTreatmentBilling(plan);

                    return (
                    <div key={plan.id} className="rounded-[26px] border border-cyan-100 bg-[linear-gradient(180deg,#ffffff,#f8fcff)] p-4 shadow-[0_14px_36px_rgba(8,145,178,0.08)]">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-lg font-semibold text-slate-950">
                              {plan.status === "completed" ? "Completed Session" : "Active Session"}
                            </p>
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-medium ${
                                plan.status === "completed"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-blue-50 text-blue-700"
                              }`}
                            >
                              {plan.status === "completed" ? "Completed" : "Active"}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-slate-500">
                            Added {formatDate(plan.createdAt)}
                          </p>
                          <div className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-800">
                            <Stethoscope size={14} />
                            Started days: {planBilling.sessionCount}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {plan.status === "active" ? (
                            <button
                              type="button"
                              onClick={() =>
                                setTreatmentActionConfirm({ type: "complete", planId: plan.id })
                              }
                              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                            >
                              <CheckCircle2 size={16} />
                              Mark Completed
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setTreatmentActionConfirm({ type: "delete", planId: plan.id })}
                            className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-600 hover:bg-rose-100"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {(plan.treatmentTypes || []).length ? (plan.treatmentTypes || []).map((type) => (
                          <span
                            key={`${plan.id}-${type}`}
                            className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
                          >
                            {type}
                          </span>
                        )) : (
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-500">
                            No treatment done details added yet
                          </span>
                        )}
                      </div>

                      <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-400">Treatment Mode</p>
                            <div className="mt-2 flex items-center gap-2 text-sm font-medium text-slate-900">
                              {String(plan.treatmentLocation || "").toLowerCase() === "home" ? (
                                <House size={16} className="text-cyan-700" />
                              ) : (
                                <Building2 size={16} className="text-cyan-700" />
                              )}
                              {String(plan.treatmentLocation || "").toLowerCase() === "home"
                                ? "Home Visit"
                                : "Clinic Visit"}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[
                              ["clinic", "Clinic"],
                              ["home", "Home"],
                            ].map(([mode, label]) => {
                              const activeMode =
                                String(plan.treatmentLocation || "clinic").toLowerCase() === mode;

                              return (
                                <button
                                  key={`${plan.id}-${mode}`}
                                  type="button"
                                  onClick={() => handleTreatmentModeChange(plan.id, mode)}
                                  disabled={plan.status === "completed"}
                                  className={`rounded-xl px-4 py-2 text-xs font-semibold ${
                                    activeMode
                                      ? "bg-cyan-700 text-white shadow-sm"
                                      : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Session Days</p>
                            <p className="text-xs text-slate-500">
                              Add date, treatment done type, and the staff who completed it.
                            </p>
                          </div>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            {plan.sessionDays?.length || 0} days
                          </span>
                        </div>

                        {(plan.status === "active" ||
                          (editingSessionDayId && sessionEntryForm.planId === plan.id)) && (
                          <form
                            onSubmit={(event) => handleSaveSessionEntry(event, plan.id)}
                            className="mt-4 rounded-2xl border border-cyan-100 bg-cyan-50/50 p-3"
                          >
                            {editingSessionDayId && sessionEntryForm.planId === plan.id ? (
                              <div className="mb-3 flex items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-cyan-900">
                                  Edit session entry
                                </p>
                                <button
                                  type="button"
                                  onClick={() => resetSessionEntryForm(plan.id)}
                                  className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : null}
                            <div className="grid gap-3 md:grid-cols-[0.8fr_1.2fr_1fr_auto] md:items-end">
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Date
                                </label>
                                <input
                                  type="date"
                                  max={todayKey}
                                  className="input mt-2 bg-white"
                                  value={
                                    sessionEntryForm.planId === plan.id
                                      ? sessionEntryForm.date
                                      : getTodayKey()
                                  }
                                  onChange={(event) =>
                                    setSessionEntryForm((current) => ({
                                      ...current,
                                      planId: plan.id,
                                      date: event.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Treatment Done
                                </label>
                                <input
                                  className="input mt-2 bg-white"
                                  placeholder="IFT, Exercise, Manual therapy..."
                                  value={
                                    sessionEntryForm.planId === plan.id
                                      ? sessionEntryForm.treatmentType
                                      : ""
                                  }
                                  onChange={(event) =>
                                    setSessionEntryForm((current) => ({
                                      ...current,
                                      planId: plan.id,
                                      treatmentType: event.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Done By Staff
                                </label>
                                <select
                                  className="input mt-2 bg-white"
                                  value={
                                    sessionEntryForm.planId === plan.id
                                      ? sessionEntryForm.doneByStaffId
                                      : ""
                                  }
                                  onChange={(event) =>
                                    setSessionEntryForm((current) => ({
                                      ...current,
                                      planId: plan.id,
                                      doneByStaffId: event.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Select staff</option>
                                  {staffOptions.map((staff) => (
                                    <option key={staff.id} value={staff.id}>
                                      {staff.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Visit Type
                                </label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                  {[
                                    ["clinic", "Clinic"],
                                    ["home", "Home"],
                                  ].map(([value, label]) => {
                                    const active =
                                      (sessionEntryForm.planId === plan.id
                                        ? sessionEntryForm.treatmentLocation
                                        : plan.treatmentLocation || "clinic") === value;
                                    return (
                                      <button
                                        key={value}
                                        type="button"
                                        onClick={() =>
                                          setSessionEntryForm((current) => ({
                                            ...current,
                                            planId: plan.id,
                                            treatmentLocation: value,
                                          }))
                                        }
                                        className={`rounded-xl border px-3 py-2 text-xs font-semibold ${
                                          active
                                            ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                                            : "border-slate-200 bg-white text-slate-500"
                                        }`}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <button
                                type="submit"
                                className="inline-flex h-11 items-center justify-center rounded-xl bg-cyan-700 px-4 text-sm font-semibold text-white hover:bg-cyan-800"
                              >
                                {editingSessionDayId && sessionEntryForm.planId === plan.id
                                  ? "Update"
                                  : "Add"}
                              </button>
                            </div>
                          </form>
                        )}

                        {plan.sessionDays?.length ? (
                          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                            {plan.sessionDays.map((day) => {
                              return (
                                <div
                                  key={day.id}
                                  className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-slate-800">
                                      {day.date || "Date not added"}
                                    </p>
                                    <p className="mt-1 truncate text-[11px] text-slate-500">
                                      {day.treatmentType || "Treatment detail not added"}
                                    </p>
                                    <p className="mt-0.5 truncate text-[11px] text-slate-400">
                                      {day.doneByStaffName ? `By ${day.doneByStaffName}` : "Staff not added"}
                                    </p>
                                    <p className="mt-0.5 truncate text-[11px] font-semibold text-cyan-700">
                                      {String(day.treatmentLocation || plan.treatmentLocation || "clinic").toLowerCase() === "home"
                                        ? "Home visit"
                                        : "Clinic visit"}
                                    </p>
                                  </div>
                                  <div className="flex shrink-0 flex-col items-end gap-2">
                                    <span className="rounded-full bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700">
                                      Added
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => startEditingSessionEntry(plan, day)}
                                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-cyan-200 hover:bg-cyan-50 hover:text-cyan-800"
                                      title="Edit session entry"
                                    >
                                      <Pencil size={12} />
                                      Edit
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                            No treatment done entries yet. Add the first date-wise treatment detail above.
                          </p>
                        )}
                      </div>

                    </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className={`${panelClass} p-4`}>
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Payments</h2>
                  <p className="text-sm text-slate-500">
                    Track treatment payments separately from session progress.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const targetPlan =
                        selectedPaymentPlan ||
                        treatmentPlans.find((plan) => plan.status === "active") ||
                        treatmentPlans[0];
                      if (targetPlan) {
                        openBillingSettingsModal(targetPlan);
                      }
                    }}
                    disabled={!treatmentPlans.length}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Settings size={14} />
                    Settings
                  </button>
                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    <IndianRupee size={14} />
                    {formatMoney(treatmentPaidTotal)} collected
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Total Paid
                  </p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">
                    {formatMoney(treatmentPaidTotal)}
                  </p>
                </div>
                <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Due Balance
                  </p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">
                    {formatMoney(treatmentBalanceTotal)}
                  </p>
                </div>
                <div className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
                    Available Balance
                  </p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">
                    {formatMoney(treatmentAvailableTotal)}
                  </p>
                </div>
                <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Days Avail
                  </p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">
                    {treatmentAvailableDays}
                  </p>
                </div>
              </div>

              {!treatmentPlans.length ? (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Start a treatment session before adding payments.
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <form
                    onSubmit={handleAddPlanPayment}
                    className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] p-4"
                  >
                    <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto] lg:items-end">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Treatment Plan
                        </label>
                        <select
                          className="input mt-2"
                          value={planPaymentForm.planId}
                          onChange={(e) => {
                            const selectedPlan = treatmentPlans.find((plan) => plan.id === e.target.value);
                            const billing = selectedPlan ? calculateTreatmentBilling(selectedPlan) : null;
                            setPlanPaymentForm({
                              planId: e.target.value,
                              paymentId: "",
                              amount: billing?.balanceAmount ? String(Math.round(billing.balanceAmount)) : "",
                              method: selectedPlan?.paymentMethod || "",
                              paymentDate: getTodayKey(),
                            });
                          }}
                        >
                          <option value="">Select plan</option>
                          {treatmentPlans.map((plan, index) => (
                            <option key={plan.id} value={plan.id}>
                              Plan {index + 1} - {plan.status === "completed" ? "Completed" : "Active"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Amount
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="input mt-2"
                          placeholder="Amount"
                          value={planPaymentForm.amount}
                          onChange={(e) =>
                            setPlanPaymentForm((current) => ({
                              ...current,
                              amount: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Payment Date
                        </label>
                        <input
                          type="date"
                          className="input mt-2"
                          value={planPaymentForm.paymentDate || getTodayKey()}
                          onChange={(e) =>
                            setPlanPaymentForm((current) => ({
                              ...current,
                              paymentDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Method
                        </label>
                        <select
                          className="input mt-2"
                          value={planPaymentForm.method}
                          onChange={(e) =>
                            setPlanPaymentForm((current) => ({
                              ...current,
                              method: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select method</option>
                          <option value="cash">Cash</option>
                          <option value="online">Online</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        disabled={!planPaymentForm.planId || !planPaymentForm.amount}
                        className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                      >
                        {planPaymentForm.paymentId ? "Update Payment" : "Add Payment"}
                      </button>
                      {planPaymentForm.paymentId ? (
                        <button
                          type="button"
                          onClick={() =>
                            setPlanPaymentForm({
                              planId: "",
                              paymentId: "",
                              amount: "",
                              method: "",
                              paymentDate: getTodayKey(),
                            })
                          }
                          className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                    {selectedPaymentBilling ? (
                      <div className="mt-4 grid gap-2 md:grid-cols-3">
                        <div className="rounded-xl bg-cyan-50 px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase text-cyan-700">Due Balance</p>
                          <p className="text-sm font-bold text-slate-900">
                            {formatMoney(selectedPaymentBilling.balanceAmount)}
                          </p>
                        </div>
                        <div className="rounded-xl bg-emerald-50 px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase text-emerald-700">Available Balance</p>
                          <p className="text-sm font-bold text-slate-900">
                            {formatMoney(selectedPaymentBilling.availableBalance)}
                          </p>
                        </div>
                        <div className="rounded-xl bg-blue-50 px-3 py-2">
                          <p className="text-[11px] font-semibold uppercase text-blue-700">Days Avail</p>
                          <p className="text-sm font-bold text-slate-900">
                            {selectedPaymentBilling.availableSessionDays}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </form>

                  <div className="space-y-3">
                    {treatmentPlans.map((plan, index) => {
                      const planBilling = calculateTreatmentBilling(plan);

                      return (
                      <div
                        key={plan.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">Plan {index + 1}</p>
                              <span
                                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                  plan.status === "completed"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-blue-50 text-blue-700"
                                }`}
                              >
                                {plan.status === "completed" ? "Completed" : "Active"}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {(plan.treatmentTypes || []).join(", ") || "Treatment plan"}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-right text-sm sm:grid-cols-4">
                            <div className="rounded-xl bg-cyan-50 px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-cyan-700">Payable</p>
                              <p className="font-semibold text-slate-900">
                                {formatMoney(planBilling.payableAmount)}
                              </p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400">Paid</p>
                              <p className="font-semibold text-slate-900">
                                {formatMoney(planBilling.paidAmount)}
                              </p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400">Due Balance</p>
                              <p className="font-semibold text-slate-900">
                                {formatMoney(planBilling.balanceAmount)}
                              </p>
                            </div>
                            <div className="rounded-xl bg-emerald-50 px-3 py-2">
                              <p className="text-[11px] uppercase tracking-wide text-emerald-700">Avail</p>
                              <p className="font-semibold text-slate-900">
                                {formatMoney(planBilling.availableBalance)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {plan.paymentNotes ? (
                          <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                            {plan.paymentNotes}
                          </p>
                        ) : null}

                        <div className="mt-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                            Payment History
                          </p>
                          {plan.payments?.length ? (
                            <div className="mt-3 grid gap-2 md:grid-cols-2">
                              {plan.payments.map((payment) => (
                                <div
                                  key={payment.id}
                                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                                >
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                      {formatMoney(payment.amount)}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {payment.method || plan.paymentMethod || "Method not added"}
                                    </p>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    <p className="text-xs text-slate-400">
                                      {payment.paymentDate
                                        ? formatDateOnly(payment.paymentDate)
                                        : formatDate(payment.createdAt)}
                                    </p>
                                    {canEditPayments ? (
                                      <button
                                        type="button"
                                        onClick={() => handleEditPlanPayment(plan, payment)}
                                        className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                        title="Edit payment"
                                      >
                                        <Pencil size={14} />
                                      </button>
                                    ) : null}
                                    {canDeletePayments ? (
                                      <button
                                        type="button"
                                        onClick={() => handleDeletePlanPayment(plan.id, payment.id)}
                                        className="rounded-full border border-red-100 bg-white p-2 text-red-500 transition hover:bg-red-50"
                                        title="Delete payment"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                              No payment entries yet.
                            </p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            <section className={`${panelClass} p-4`}>
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Clinical Notes</h2>
                  <p className="text-sm text-slate-500">
                    Keep each clinical note type once, then update it as treatment progresses.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowClinicalDocumentModal(true)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Files size={15} />
                    Add Document
                  </button>
                  <button
                    onClick={() => setShowClinicalNoteModal(true)}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  >
                    <Plus size={15} />
                    Add Note
                  </button>
                </div>
              </div>

              {patient.disease ? (
                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Primary Condition</p>
                  <p className="mt-2 text-slate-900">{patient.disease}</p>
                </div>
              ) : null}

              {patient.notes ? (
                <div className="mb-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Legacy Notes</p>
                  <p className="mt-2 whitespace-pre-wrap text-slate-700">{patient.notes}</p>
                </div>
              ) : null}

              <div className="space-y-2">
                {!patient.clinicalNotes?.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    No clinical notes added yet.
                  </div>
                ) : (
                  patient.clinicalNotes.map((entry) => (
                    <div key={entry.id} className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 gap-3">
                          <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                            <FileText size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-900">
                                {entry.title || "Clinical Note"}
                              </p>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                {entry.addedByLabel || (entry.addedByType === "patient" ? "Patient" : "OPW")}
                              </span>
                            </div>
                            <p className="mt-0.5 text-xs text-slate-400">
                              {formatDate(entry.createdAt)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteClinicalNote(entry.id)}
                          className="shrink-0 rounded-xl p-2 text-rose-500 hover:bg-rose-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                        {entry.note || "No note text added."}
                      </p>

                    </div>
                  ))
                )}
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">Clinical Documents</h3>
                    <p className="text-xs text-slate-500">Images and PDF files saved separately.</p>
                  </div>
                </div>
                {patient.clinicalDocuments?.length ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {patient.clinicalDocuments.map((document) => {
                      const isImage = document.mimeType?.startsWith("image/");
                      const canDeleteDocument = document.source !== "legacy_note_document";
                      return (
                        <div
                          key={`${document.source}-${document.id}`}
                          className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
                        >
                          <a
                            href={`${API.defaults.baseURL}${document.downloadUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700"
                          >
                            {isImage ? <Image size={15} /> : <Download size={15} />}
                            <span className="truncate">{document.name}</span>
                          </a>
                          {canDeleteDocument ? (
                            <button
                              type="button"
                              onClick={() => handleDeleteClinicalDocument(document.id)}
                              className="shrink-0 rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"
                            >
                              <Trash2 size={14} />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-sm text-slate-500">
                    No clinical documents uploaded yet.
                  </p>
                )}
              </div>
            </section>

            <section className={`${panelClass} p-4`}>
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Recommended Therapy</h2>
                  <p className="text-sm text-slate-500">
                    Add service-wise therapy items for this patient. These will also appear on the patient therapy tab.
                  </p>
                </div>
                <button
                  onClick={openTherapyModal}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  <Plus size={15} />
                  Add Therapy
                </button>
              </div>

              <div className="space-y-3">
                {!patient.therapyRecommendations?.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    No therapy recommendations added yet.
                  </div>
                ) : (
                  patient.therapyRecommendations.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-base font-semibold text-slate-900">
                              {entry.serviceName || "Therapy Service"}
                            </p>
                            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                              {(entry.items || []).length} item
                              {(entry.items || []).length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-400">
                            Updated {formatDate(entry.updatedAt || entry.createdAt)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteTherapyRecommendation(entry.id)}
                          className="shrink-0 rounded-xl p-2 text-rose-500 hover:bg-rose-50"
                          title="Delete therapy recommendation"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {entry.note ? (
                        <p className="mt-3 rounded-2xl bg-slate-50 px-3 py-3 text-sm leading-6 text-slate-700">
                          {entry.note}
                        </p>
                      ) : null}

                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {(entry.items || []).map((item) => (
                          <div
                            key={item.id}
                            className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900">
                                  {item.title || item.fileName || "Therapy Item"}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {item.fileName || "No file name"}
                                </p>
                              </div>
                              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold uppercase text-slate-600">
                                {item.resourceType || "file"}
                              </span>
                            </div>
                            {item.description ? (
                              <p className="mt-2 text-sm leading-6 text-slate-600">
                                {item.description}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          <div className="min-w-0 space-y-4 lg:sticky lg:top-4">
            <section className={`${panelClass} overflow-hidden p-4`}>
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Appointments</h2>
                  <p className="text-sm text-slate-500">Requests, scheduled appointments, and patient-visible history.</p>
                </div>
                <button
                  onClick={() => setShowAppointmentModal(true)}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <Plus size={15} />
                  Add
                </button>
              </div>

              <div className="mb-5 rounded-2xl border border-amber-100 bg-amber-50/50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      Appointment Requests
                    </h3>
                    <p className="text-sm text-slate-500">
                      Requests raised by this patient before they become booked appointments.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                    {visibleAppointmentRequests.length} requests
                  </span>
                </div>

                <div className="space-y-3">
                  {visibleAppointmentRequests.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-amber-200 bg-white/80 px-4 py-6 text-center text-sm text-slate-500">
                      No separate appointment requests.
                    </div>
                  ) : (
                    visibleAppointmentRequests.map((request) => {
                      const requestForm = appointmentRequestForms[request.id] || {
                        date: request.requestedDate || "",
                        time: request.requestedTime || "",
                        note: request.decisionNote || "",
                      };

                      return (
                        <div
                          key={request.id}
                          className="rounded-2xl border border-amber-100 bg-white p-4 shadow-sm"
                        >
                          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-900">
                                  {request.service || "Appointment"}
                                </p>
                                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                  Pending
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-slate-500">
                                Requested: {request.requestedDate || "Date not added"}
                                {request.requestedTime ? ` at ${request.requestedTime}` : ""}
                              </p>
                              <p className="mt-1 text-xs font-semibold text-sky-700">
                                Service location: {request.serviceLocationLabel || formatServiceLocation(request.serviceLocation)}
                              </p>
                              <p className="mt-1 text-xs text-slate-400">
                                {request.phone || request.email || "No contact"}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
                            <input
                              type="date"
                              value={requestForm.date}
                              onChange={(event) =>
                                updateAppointmentRequestForm(
                                  request.id,
                                  "date",
                                  event.target.value
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100"
                            />
                            <input
                              type="time"
                              value={requestForm.time}
                              onChange={(event) =>
                                updateAppointmentRequestForm(
                                  request.id,
                                  "time",
                                  event.target.value
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100"
                            />
                            <input
                              value={requestForm.note}
                              onChange={(event) =>
                                updateAppointmentRequestForm(
                                  request.id,
                                  "note",
                                  event.target.value
                                )
                              }
                              placeholder="OPW note"
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 sm:col-span-2"
                            />
                            <button
                              type="button"
                              onClick={() => handleAppointmentRequestDecision(request, "approve")}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              <CheckCircle2 size={15} />
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAppointmentRequestDecision(request, "reschedule")}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700"
                            >
                              <RotateCcw size={15} />
                              Reschedule
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {activeAppointments.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
                    No active appointment scheduled.
                  </div>
                ) : (
                  activeAppointments.map((appointment) => {
                    const actionForm = appointmentActionForms[appointment.id] || {
                      date: appointment.date || "",
                      time: appointment.time || "",
                      remark: appointment.remark || "",
                    };

                    return (
                      <div
                        key={appointment.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-slate-900">{appointment.service}</p>
                              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                                {getAppointmentStatusLabel(appointment.status)}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {appointment.date}
                              {appointment.time ? ` at ${appointment.time}` : ""}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-sky-700">
                              Service location: {appointment.serviceLocationLabel || formatServiceLocation(appointment.serviceLocation)}
                            </p>
                            {appointment.remark ? (
                              <p className="mt-1 text-xs text-slate-500">
                                OPW remark: {appointment.remark}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
                          <input
                            type="date"
                            value={actionForm.date}
                            onChange={(event) =>
                              updateAppointmentActionForm(
                                appointment.id,
                                "date",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100"
                          />
                          <input
                            type="time"
                            value={actionForm.time}
                            onChange={(event) =>
                              updateAppointmentActionForm(
                                appointment.id,
                                "time",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100"
                          />
                          <input
                            value={actionForm.remark}
                            onChange={(event) =>
                              updateAppointmentActionForm(
                                appointment.id,
                                "remark",
                                event.target.value
                              )
                            }
                            placeholder="Remark"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 sm:col-span-2"
                          />
                          <button
                            type="button"
                            onClick={() => handleAppointmentStatus(appointment, "rescheduled")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700"
                          >
                            <RotateCcw size={15} />
                            Reschedule
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAppointmentStatus(appointment, "completed")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                          >
                            <CheckCircle2 size={15} />
                            Done
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAppointmentStatus(appointment, "cancelled")}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700 sm:col-span-2"
                          >
                            <XCircle size={15} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="mt-5 border-t border-slate-100 pt-5">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-slate-900">Appointment History</h3>
                  <p className="text-sm text-slate-500">
                    Completed and cancelled appointment updates visible to the patient.
                  </p>
                </div>

                <div className="space-y-3">
                  {appointmentHistory.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      No appointment history yet.
                    </div>
                  ) : (
                    appointmentHistory.map((appointment) => (
                      <div
                        key={appointment.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-slate-900">{appointment.service}</p>
                              <span
                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  appointment.status === "cancelled"
                                    ? "bg-rose-50 text-rose-700"
                                    : "bg-slate-900 text-white"
                                }`}
                              >
                                {getAppointmentStatusLabel(appointment.status)}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {appointment.date}
                              {appointment.time ? ` at ${appointment.time}` : ""}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-sky-700">
                              Service location: {appointment.serviceLocationLabel || formatServiceLocation(appointment.serviceLocation)}
                            </p>
                          </div>
                          {appointment.remark ? (
                            <p className="rounded-xl bg-white px-3 py-2 text-xs font-medium text-slate-600">
                              OPW remark: {appointment.remark}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>

      {[editBasic, showClinicalNoteModal, showClinicalDocumentModal, showTherapyModal, showAppointmentModal, showTreatmentModal].some(Boolean) && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 px-4 py-6 backdrop-blur-sm"
          onClick={closeDrawer}
        >
          <div
            className={`relative mx-auto w-full rounded-[28px] border border-white/70 bg-white p-5 shadow-2xl shadow-slate-950/30 ${
              showTreatmentModal || showTherapyModal ? "max-w-3xl" : "max-w-lg"
            }`}
            style={{ marginTop: "max(1.5rem, env(safe-area-inset-top))", marginBottom: "1.5rem" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`mb-6 flex items-start justify-between ${
                showTreatmentModal
                  ? "rounded-[24px] bg-gradient-to-br from-slate-950 via-cyan-950 to-sky-800 p-5 text-white"
                  : "rounded-[24px] border border-slate-200 bg-slate-50 p-4"
              }`}
            >
              <div>
                <p className={`text-sm font-medium ${showTreatmentModal ? "text-cyan-100" : "text-blue-600"}`}>
                  Patient Update
                </p>
                <h3 className={`text-2xl font-semibold ${showTreatmentModal ? "text-white" : "text-slate-900"}`}>
                  {editBasic && "Edit Patient"}
                  {showClinicalNoteModal && "Clinical Notes"}
                  {showClinicalDocumentModal && "Clinical Documents"}
                  {showTherapyModal && "Recommended Therapy"}
                  {showAppointmentModal && "Add Appointment"}
                  {showTreatmentModal && (editingPlanId ? "Edit Session" : "Session Start")}
                </h3>
                {showTreatmentModal ? (
                  <p className="mt-2 text-sm text-cyan-50/80">
                    {editingPlanId
                      ? "Extend date range or update treatment details. Existing daily status stays safe."
                      : "Only one active treatment session can run for a patient at a time."}
                  </p>
                ) : null}
              </div>

              <button
                className={`rounded-xl border p-2 ${
                  showTreatmentModal
                    ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                }`}
                onClick={closeDrawer}
              >
                <X size={18} />
              </button>
            </div>

            {editBasic && (
              <form onSubmit={handleBasicUpdate} className="space-y-4">
                <input
                  className="input"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Mobile number"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                />
                <textarea
                  className="input min-h-[96px] resize-none"
                  placeholder="Address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
                <button className="w-full rounded-xl bg-slate-900 px-4 py-3 font-medium text-white hover:bg-slate-800">
                  Save Changes
                </button>
              </form>
            )}

            {showTreatmentModal && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (editingPlanId) {
                    handleUpdateTreatment(event);
                    return;
                  }
                  handleStartTreatment();
                }}
                className="space-y-5"
              >
                <div className="rounded-3xl border border-cyan-100 bg-cyan-50/60 p-4">
                  <p className="text-sm font-semibold text-slate-900">Treatment Mode</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Choose whether the active session is handled at clinic or as a home visit.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {[
                      ["clinic", "Clinic"],
                      ["home", "Home Visit"],
                    ].map(([mode, label]) => {
                      const activeMode = treatmentForm.treatmentLocation === mode;

                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() =>
                            setTreatmentForm((current) => ({
                              ...current,
                              treatmentLocation: mode,
                            }))
                          }
                          className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                            activeMode
                              ? "border-cyan-700 bg-cyan-700 text-white shadow-lg shadow-cyan-700/20"
                              : "border-slate-200 bg-white text-slate-600 hover:border-cyan-200"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button className="w-full rounded-2xl bg-cyan-700 px-4 py-3 font-semibold text-white shadow-lg shadow-cyan-700/20 hover:bg-cyan-800">
                  {editingPlanId ? "Save Treatment Mode" : "Start Session"}
                </button>
              </form>
            )}

            {showClinicalNoteModal && (
              <form onSubmit={handleAddClinicalNote} className="space-y-4">
                <select
                  className="input"
                  value={clinicalNoteForm.title}
                  onChange={(e) =>
                    setClinicalNoteForm((current) => ({
                      ...current,
                      title: e.target.value,
                    }))
                  }
                >
                  {CLINICAL_NOTE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <textarea
                  className="input min-h-[160px]"
                  placeholder="Clinical note"
                  value={clinicalNoteForm.note}
                  onChange={(e) =>
                    setClinicalNoteForm((current) => ({
                      ...current,
                      note: e.target.value,
                    }))
                  }
                />
                <button className="w-full rounded-xl bg-slate-900 px-4 py-3 font-medium text-white hover:bg-slate-800">
                  Save Note
                </button>
              </form>
            )}

            {showClinicalDocumentModal && (
              <form onSubmit={handleAddClinicalDocuments} className="space-y-4">
                <div className="rounded-2xl border border-dashed border-slate-300 p-4">
                  <label className="block text-sm font-medium text-slate-700">
                    Upload images or PDF files
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf,application/pdf"
                    multiple
                    className="mt-3 block w-full text-sm text-slate-600"
                    onChange={(e) => setClinicalDocumentFiles(Array.from(e.target.files || []))}
                  />
                  {clinicalDocumentFiles.length ? (
                    <div className="mt-3 space-y-2">
                      {clinicalDocumentFiles.map((file) => (
                        <p key={`${file.name}-${file.size}`} className="text-sm text-slate-500">
                          {file.name}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
                <button className="w-full rounded-xl bg-slate-900 px-4 py-3 font-medium text-white hover:bg-slate-800">
                  Save Documents
                </button>
              </form>
            )}

            {showTherapyModal && (
              <form onSubmit={handleSaveTherapyRecommendation} className="space-y-5">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <label className="mb-2 block text-sm font-semibold text-slate-900">
                    Service
                  </label>
                  <select
                    className="input bg-white"
                    value={therapyForm.serviceId}
                    onChange={(event) =>
                      setTherapyForm((current) => ({
                        ...current,
                        serviceId: event.target.value,
                        itemIds: [],
                      }))
                    }
                    required
                  >
                    <option value="">Select service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>

                  <textarea
                    className="input mt-4 min-h-[110px] bg-white"
                    placeholder="Doctor recommendation note for this therapy"
                    value={therapyForm.note}
                    onChange={(event) =>
                      setTherapyForm((current) => ({
                        ...current,
                        note: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Therapy Items</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Choose multiple therapy items for the selected service.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      {therapyForm.itemIds.length} selected
                    </span>
                  </div>

                  {!therapyForm.serviceId ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      Select a service first to view therapy items.
                    </div>
                  ) : !availableTherapyResources.length ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No therapy items are available for this service yet.
                    </div>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      {availableTherapyResources.map((resource) => {
                        const checked = therapyForm.itemIds.includes(resource.id);

                        return (
                          <label
                            key={resource.id}
                            className={`flex cursor-pointer gap-3 rounded-2xl border px-4 py-4 ${
                              checked
                                ? "border-sky-300 bg-sky-50"
                                : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTherapyItem(resource.id)}
                              className="mt-1"
                            />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-900">
                                  {resource.title || resource.fileName}
                                </p>
                                <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold uppercase text-slate-600">
                                  {resource.resourceType || "file"}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-slate-500">
                                {resource.fileName}
                              </p>
                              {resource.description ? (
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                  {resource.description}
                                </p>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button className="w-full rounded-xl bg-slate-900 px-4 py-3 font-medium text-white hover:bg-slate-800">
                  Save Therapy Recommendation
                </button>
              </form>
            )}

            {showAppointmentModal && (
              <form onSubmit={handleAddAppointment} className="space-y-4">
                <input
                  type="date"
                  className="input"
                  value={appointmentForm.date}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      date: e.target.value,
                    })
                  }
                />
                <input
                  type="time"
                  className="input"
                  value={appointmentForm.time}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      time: e.target.value,
                    })
                  }
                />
                <select
                  className="input"
                  value={appointmentForm.service}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      service: e.target.value,
                    })
                  }
                >
                  <option value="">Select service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                </select>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="mb-3 text-sm font-semibold text-slate-700">
                    Service location
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {[
                      ["clinic", "At clinic"],
                      ["home", "At home"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setAppointmentForm({
                            ...appointmentForm,
                            serviceLocation: value,
                          })
                        }
                        className={`rounded-xl border px-3 py-2 text-left text-sm font-semibold ${
                          appointmentForm.serviceLocation === value
                            ? "border-sky-300 bg-sky-50 text-sky-800"
                            : "border-slate-200 bg-white text-slate-600"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 rounded-xl bg-sky-50 px-3 py-2 text-xs font-semibold leading-5 text-sky-800">
                    {appointmentLocationNote}
                  </p>
                </div>
                <button className="w-full rounded-xl bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700">
                  Save Appointment
                </button>
              </form>
            )}

          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
